import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { Heart, MessageCircle, Share } from "lucide-react";
import { addComment, fetchApprovedForumsWithId, likeForum } from "../utils/api";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import * as moment from "moment";

const ForumDetails = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const id = useParams()['id']
    const commentsRef = useRef(null);

    const [isLiked, setIsLiked] = useState(false);
    const [likesCount, setLikesCount] = useState(0);
    const [likeLoader, setLikeLoader] = useState(false);

    const [comment, setComment] = useState({
        text: "",
    })

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") setPreviewImage(null);
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, []);






    const { data: forums, isLoading, refetch } = useQuery({
        queryKey: ["forummain", id],
        queryFn: () => fetchApprovedForumsWithId(id),
        enabled: !!id
    })

    const toggleLike = async () => {
        if (likeLoader) return;

        const prevLiked = isLiked;
        const prevCount = likesCount;

        // 🔥 optimistic update
        setIsLiked(!prevLiked);
        setLikesCount(prevLiked ? prevCount - 1 : prevCount + 1);

        setLikeLoader(true);

        try {
            const response = await likeForum(id);
        } catch (error) {
            // ❌ rollback on failure
            setIsLiked(prevLiked);
            setLikesCount(prevCount);
            toast.error(error.message || "Like failed");
        } finally {
            setLikeLoader(false);
        }
    };



    const handleShare = async () => {
        const shareUrl = window.location.href;
        const title = forums?.data?.title || "Check this forum post";
        const text = forums?.data?.description?.slice(0, 100);

        // ✅ Native Share (mobile, modern browsers)
        if (navigator.share) {
            try {
                await navigator.share({
                    title,
                    text,
                    url: shareUrl,
                });
            } catch (error) {
                console.log("Share cancelled", error);
            }
        } else {
            // 🔁 Fallback: Copy to clipboard
            await navigator.clipboard.writeText(shareUrl);
            alert("Link copied to clipboard!");
        }
    };

    useEffect(() => {
        if (forums) {
            setIsLiked(forums.isLiked);
            setLikesCount(forums.likesCount);
        }
    }, [forums]);






    useEffect(() => {
        console.log(forums)
    }, [forums])



    return (
        <>
            {isLoading ? <ForumDetailsSkeleton /> :
                <div className="container mx-auto max-w-6xl px-7 sm:px-6 lg:px-8 py-6">

                    <div className="mx-auto max-w-4xl lg:max-w-6xl">

                        {/* Post Header */}
                        <div className="text-xs text-gray-500 mb-2">
                            {forums?.data?.startupId?.startupName || "Tactos"} ·{" "}
                            {moment(forums?.data?.createdAt).fromNow()}
                        </div>

                        {/* Title */}
                        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
                            {forums?.data?.title}
                        </h1>

                        {forums?.data?.images?.length > 0 && (
                            <div
                                className={`
    mb-6 flex flex-col
    ${forums.data.images.length === 1
                                        ? "lg:h-[350px]"
                                        : "lg:h-[400px] lg:grid lg:grid-cols-3 lg:grid-rows-2 gap-2"}
  `}
                            >
                                {/* LEFT / MAIN IMAGE */}
                                <div
                                    className={`
        bg-gray-200 rounded-xl overflow-hidden
        ${forums.data.images.length === 1
                                            ? "w-full h-full"
                                            : "lg:col-span-2 lg:row-span-2 h-[250px] lg:h-full"}
      `}
                                >
                                    <img
                                        src={forums.data.images[0]}
                                        alt="forum image 1"
                                        onClick={() => setPreviewImage(forums.data.images[0])}
                                        className="w-full h-full object-cover cursor-pointer"
                                    />
                                </div>

                                {/* RIGHT SIDE IMAGES */}
                                {forums.data.images.length > 1 && (
                                    <div className="flex lg:contents gap-2 mt-2 lg:mt-0">

                                        {forums.data.images[1] && (
                                            <div className="flex-1 bg-gray-200 rounded-xl overflow-hidden">
                                                <img
                                                    src={forums.data.images[1]}
                                                    alt="forum image 2"
                                                    onClick={() => setPreviewImage(forums.data.images[1])}
                                                    className="w-full h-full object-cover cursor-pointer"
                                                />
                                            </div>
                                        )}

                                        {forums.data.images[2] && (
                                            <div className="flex-1 bg-gray-200 rounded-xl overflow-hidden">
                                                <img
                                                    src={forums.data.images[2]}
                                                    alt='sforum image 3'
                                                    onClick={() => setPreviewImage(forums.data.images[2])}
                                                    className="w-full h-full object-cover cursor-pointer"
                                                />
                                            </div>
                                        )}

                                    </div>
                                )}
                            </div>
                        )}





                        {/* Description */}
                        <p className="text-gray-800 text-sm sm:text-base leading-relaxed mb-6">
                            {forums?.data?.description}
                        </p>

                        {/* Meta */}
                        <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-600 mb-6">

                            <span className="flex items-center gap-1">

                                <Heart
                                    onClick={toggleLike}
                                    className={`cursor-pointer transition-colors ${isLiked ? "text-red-500 fill-red-500" : "text-gray-600"
                                        }`}
                                />

                                <span>{likesCount} Likes</span>
                            </span>

                            <span onClick={() =>
                                commentsRef.current?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "start",
                                })
                            } className="flex items-center gap-1 hover:text-[#fd3e3d] cursor-pointer">
                                <MessageCircle /> {forums?.data?.comments?.length} Comments
                            </span>

                            <span
                                onClick={handleShare}
                                className="flex items-center gap-1 cursor-pointer hover:text-[#fd3e3d]"
                            >
                                <Share /> Share
                            </span>
                        </div>

                        {/* Comment Box */}
                        <div ref={commentsRef}>
                            <CommentBox forumID={id} refetch={refetch} />
                        </div>


                        {/* Comments */}
                        <div className="mt-8">
                            <h2 className="text-lg font-semibold mb-4">Comments</h2>

                            <div className="space-y-6">
                                {forums?.data?.comments?.length > 0 ? (
                                    forums.data.comments.map((comment) => (
                                        <div key={comment._id} className="flex gap-3">
                                            <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-[10px] text-gray-600">
                                                {comment?.startupId?.startupName?.charAt(0).toUpperCase()}
                                            </div>

                                            <div className="flex-1">
                                                <div className="text-xs text-gray-500 mb-1">
                                                    {comment.startupId?.startupName} ·{" "}
                                                    {moment(comment.createdAt).fromNow()}
                                                </div>
                                                <p className="text-sm text-gray-800">
                                                    {comment.text}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center text-sm text-gray-400 py-4">
                                        No comments found
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

            }


            <AnimatePresence>
                {previewImage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="
        fixed inset-0 z-50
        flex items-center justify-center
        bg-black/60 backdrop-blur-md
      "
                        onClick={() => setPreviewImage(null)}
                    >
                        <motion.img
                            src={previewImage}
                            alt="preview"
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                            className="
          max-w-[90vw] max-h-[90vh]
          rounded-xl shadow-2xl
        "
                            onClick={(e) => e.stopPropagation()}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};


export default ForumDetails;

const ForumDetailsSkeleton = () => {
    return (
        <div className="max-w-6xl mx-auto px-4 py-6 animate-pulse">

            {/* Subreddit */}
            <div className="h-3 w-40 bg-gray-200 rounded mb-2" />

            {/* Title */}
            <div className="h-7 w-3/4 bg-gray-200 rounded mb-4" />

            {/* Image Grid */}
            <div className="grid grid-cols-3 grid-rows-2 gap-2 mb-6 h-[400px]">
                <div className="col-span-2 row-span-2 bg-gray-200 rounded-xl" />
                <div className="bg-gray-200 rounded-xl" />
                <div className="bg-gray-200 rounded-xl" />
            </div>

            {/* Description */}
            <div className="space-y-2 mb-6">
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-11/12" />
                <div className="h-4 bg-gray-200 rounded w-10/12" />
            </div>

            {/* Meta */}
            <div className="flex gap-6 mb-6">
                <div className="h-4 w-20 bg-gray-200 rounded" />
                <div className="h-4 w-24 bg-gray-200 rounded" />
                <div className="h-4 w-16 bg-gray-200 rounded" />
            </div>

            {/* Comment Input */}
            <div className="h-12 bg-gray-200 rounded mb-6" />

            {/* Comments */}
            <div className="space-y-6">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="flex gap-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-full" />
                        <div className="flex-1 space-y-2">
                            <div className="h-3 w-32 bg-gray-200 rounded" />
                            <div className="h-4 w-full bg-gray-200 rounded" />
                            <div className="h-4 w-10/12 bg-gray-200 rounded" />
                        </div>
                    </div>
                ))}
            </div>

        </div>
    );
};


const CommentBox = ({ forumID, refetch }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [text, setText] = useState("");
    const [comment, setComment] = useState({
        text: "",
    })

    const onSendComment = async () => {
        if (!comment) return
        try {
            const responce = await addComment(forumID, comment)
            toast.success(responce.message)
            setComment({ ...comment, text: "" })
            setIsOpen(false);
            refetch()
        } catch (error) {
            toast.error(error.message)
            console.log(error)
        }
    }

    return (
        <div className="mb-8">
            {/* COLLAPSED INPUT */}
            {!isOpen && (
                <input
                    type="text"
                    placeholder="Join the conversation"
                    onClick={() => setIsOpen(true)}
                    className="
            w-full px-5 py-4
            rounded-xl
            bg-[#f6f7f8]
            border border-gray-300
            text-sm text-gray-800
            placeholder-gray-500
            cursor-text
            focus:outline-none focus:ring-0 focus:border-gray-300
            
          "
                />
            )}

            {/* EXPANDED COMMENT BOX */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98, y: -5 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98, y: -5 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="
              bg-[#f6f7f8]
              border border-gray-300
              rounded-xl
              p-4
            "
                    >
                        <textarea
                            autoFocus
                            rows={4}
                            value={comment.text}
                            onChange={(e) => setComment({ ...comment, text: e.target.value })}
                            placeholder="Join the conversation"
                            className="
                w-full resize-none
                bg-transparent
                text-sm text-gray-800
                placeholder-gray-500
                 focus:outline-none
    focus:ring-0
    focus:ring-transparent
    focus:border-red-500
              "
                        />

                        {/* ACTION BAR */}
                        <div className="flex items-center justify-between mt-4">
                            {/* Left */}


                            {/* Right */}
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => {
                                        setIsOpen(false);

                                    }}
                                    className="  px-4 py-1.5  rounded-full  bg-gray-200  text-sm text-gray-700  hover:bg-gray-300 transition
                  "
                                >
                                    Cancel
                                </button>

                                <button
                                    className=" px-4 py-1.5 rounded-full bg-[#fd3e3d] text-white text-sm hover:bg-[#c72f2f] transition
                  "            onClick={onSendComment}
                                >
                                    Comment
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>

    );
};